import { toSearchParams, type ListQuery, type ListResult } from '@redhand/ui';
import { pluralize } from '@reos/i18n';
import { baseUrl } from './api.js';
import type { AddressFromQuery } from './address-query.js';

/**
 * Klient BFF rout nemovitosti (apps/api/src/routes/properties-list.ts +
 * property kontext). Tvary drzi serverova zod schemata; tady jen typy.
 */

export interface Money {
  readonly amount: string;
  readonly currency: string;
}

/** Polozka GET /v1/properties/list dle LIST-CONTRACT. */
export interface PropertyListItem {
  readonly id: string;
  readonly name: string | null;
  readonly kind: string;
  readonly status: string;
  readonly stage: string | null;
  readonly address: string;
  readonly locality: string | null;
  readonly price: Money | null;
  readonly coverPhotoUrl: string | null;
  /** Krok UXA1: karta bez fotky ukazuje mapu/ortofoto misto prazdne ikony. */
  readonly lat: number | null;
  readonly lon: number | null;
  readonly updatedAt: string;
}

export interface PropertyAddress {
  readonly formatted: string;
  readonly lines: readonly string[];
  readonly locality: string | null;
  readonly postalCode: string | null;
}

/**
 * Krok PROP7B — VYSTUP SE SNAPSHOTEM STARE ADRESY (`changeAddressResultSchema`).
 * Podepsana smlouva a odeslany inzerat drzi adresu z doby vzniku, takze je
 * oprava nemuze prepsat — makler je musi aktualizovat sam.
 */
export interface StaleAddressOutput {
  readonly kind: 'signed_contract' | 'published_listing';
  readonly id: string;
  readonly label: string;
}

/** Odpoved PATCH /v1/properties/:id/address. */
export interface ChangeAddressResult {
  readonly property: PropertyDetail;
  readonly previousAddress: PropertyAddress | null;
  readonly changed: boolean;
  readonly staleOutputs: readonly StaleAddressOutput[];
  readonly noticeCreated: boolean;
}

/** GET /v1/properties/:id (propertyDtoSchema, property kontext). */
export interface PropertyDetail {
  readonly id: string;
  readonly kind: string;
  readonly status: string;
  readonly name: string | null;
  readonly address: PropertyAddress | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /**
   * Krok P8F1 bod 3: pocet parcel karty. `0` = „Doplňte parcelu" (druhy byt
   * v dome parcelu nedostal). Volitelny — starsi odpoved klic nemusi mit
   * a karta pak netvrdi nic.
   */
  readonly parcelCount?: number | undefined;
}

export interface WorkflowTransition {
  readonly fromState: string | null;
  readonly toState: string;
  readonly occurredAt: string;
  readonly actorId: string | null;
}

export interface PropertyWorkflow {
  readonly stage: string | null;
  readonly transitions: readonly WorkflowTransition[];
}

/**
 * Fotka nemovitosti (krok O6). `url`/`thumbUrl` chodi ze serveru bud jako cesta
 * na stream API (`/v1/properties/...`), nebo jako presigned S3 URL, kdyz bezi
 * `MEDIA_DIRECT_S3=1` — obojim projde `mediaSrc()`.
 */
export interface PropertyMediaItem {
  readonly id: string;
  readonly fileId: string;
  readonly name: string | null;
  readonly caption: string | null;
  readonly kind: string;
  /** Krok NAB3 — mistnost na fotce (`MEDIA_ROOM_TYPES`); null = neurceno. */
  readonly roomType?: string | null;
  /**
   * Krok NAP3 — kody vad kvality (tma, rozmazani, krivy horizont).
   * `null`/chybi = fotka nahrana pred NAP3, tedy NEMERENA; `[]` = bez vady.
   */
  readonly qualityIssues?: readonly string[] | null;
  /**
   * Krok STG1 — `true` = obrazek z virtualniho stagingu (AI nabytek).
   * Nepovinne: fixtury starsich testu tenhle klic nemaji a `undefined`
   * znamena „obycejna fotka".
   */
  readonly isVisualization?: boolean;
  readonly sourceMediaId?: string | null;
  readonly visualizationVariant?: 'DAY' | 'EVENING' | null;
  readonly sortOrder: number;
  readonly isCover: boolean;
  readonly contentType: string;
  readonly sizeBytes: number | null;
  readonly thumbReady: boolean;
  readonly url: string;
  readonly thumbUrl: string;
  readonly createdAt: string;
}

export interface PropertyCover {
  readonly propertyId: string;
  /** null = nemovitost zatim nema fotku. */
  readonly mediaId: string | null;
  readonly thumbUrl: string | null;
  readonly caption: string | null;
  /** Krok DEMO1: souradnice pro mapovy nahled, kdyz fotka chybi. */
  readonly lat: number | null;
  readonly lon: number | null;
}

/** Relativni cestu z API doplni o `baseUrl`; absolutni URL nechava byt. */
export function mediaSrc(url: string): string {
  return url.startsWith('http://') || url.startsWith('https://') ? url : `${baseUrl}${url}`;
}

export interface CoverSize {
  readonly width: number;
  readonly height: number;
}

/**
 * Krok UXF1 bod 2 — adresy obrazku nemovitosti z MAPY.COM. Jdou PRES API, ne
 * primo na `api.mapy.com`: klic pro tyhle sluzby zustava na serveru a server
 * k nim navic dokresli hranici parcely a povinnou atribuci (viz
 * `apps/api/src/routes/property-cover-map.ts`).
 *
 * `scale=2` = retina: server vyrobi obrazek dvojnasobny a prohlizec ho zmensi,
 * takze na karte je ostry (nalez R.B. 7. 10.: „pixelovaný výřez").
 * Rozmer se posila v CSS pixelech, aby atribuce zustala citelna.
 *
 * `<img src>` nese obrazek jako binarni GET (session cookie jde automaticky,
 * stejny puvod), ne pres `apiClient` — ten je jen pro JSON.
 */
const COVER_DEVICE_SCALE = 2;

function coverQuery(size: CoverSize): string {
  return `w=${String(Math.round(size.width))}&h=${String(Math.round(size.height))}&scale=${String(COVER_DEVICE_SCALE)}`;
}

export function propertyPanoramaCoverUrl(propertyId: string, size: CoverSize): string {
  return `${baseUrl}/v1/properties/${propertyId}/cover/panorama.jpg?${coverQuery(size)}`;
}

export function propertyAerialCoverUrl(propertyId: string, size: CoverSize): string {
  return `${baseUrl}/v1/properties/${propertyId}/cover/aerial.jpg?${coverQuery(size)}`;
}

export interface PropertyDocumentItem {
  readonly documentId: string;
  readonly name: string;
  readonly kind: string;
  readonly createdAt: string;
  readonly fileId: string | null;
}

export interface PropertyFact {
  readonly factKey: string;
  readonly value: unknown;
  readonly unit: string | null;
  /**
   * Krok P8F6 bod 3: puvod udaje (`property_facts.provenance`) — detail
   * zakazky z nej kresli stitek „katastr / tvrdí majitel / makléř".
   * Volitelny: starsi odpoved (nebo atrapa v testu) klic nemusi mit a radek
   * pak stitek nema, nez aby obrazovka tvrdila puvod, ktery nezna.
   */
  readonly provenance?:
    | { readonly status: string; readonly sourceType: string; readonly sourceRef?: string | null }
    | undefined;
}

/**
 * Poradi prodejniho procesu — zrcadlo LISTING_STAGES z @reos/schemas
 * (web-admin balik @reos/schemas nema v zavislostech; enum se meni jen ADR).
 */
export const LISTING_STAGES = ['intake', 'preparation', 'listing', 'negotiation', 'closed'] as const;

export const STAGE_LABELS: Readonly<Record<string, string>> = {
  intake: 'Nábor',
  preparation: 'Příprava',
  listing: 'Inzerce',
  negotiation: 'Jednání',
  closed: 'Uzavřeno',
};

export const KIND_VALUES = ['land', 'building', 'unit', 'complex'] as const;

export const KIND_LABELS: Readonly<Record<string, string>> = {
  land: 'Pozemek',
  building: 'Budova',
  unit: 'Jednotka',
  complex: 'Komplex',
};

export function stageLabel(stage: string | null): string {
  if (stage === null) return '—';
  return STAGE_LABELS[stage] ?? stage;
}

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

interface ProblemBody {
  readonly title?: string;
  readonly detail?: string;
  readonly traceId?: string;
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!res.ok) {
    const problem = (await res.json().catch(() => null)) as ProblemBody | null;
    const text = problem?.detail ?? problem?.title ?? `HTTP ${res.status}`;
    throw new Error(problem?.traceId ? `${text} (traceId ${problem.traceId})` : text);
  }
  return (await res.json()) as T;
}

export function fetchPropertiesList(query: ListQuery): Promise<ListResult<PropertyListItem>> {
  return fetchJson(`/v1/properties/list?${toSearchParams(query).toString()}`);
}

export async function fetchLocalities(): Promise<readonly string[]> {
  const data = await fetchJson<{ items: string[] }>('/v1/properties/localities');
  return data.items;
}

export function fetchPropertyDetail(id: string): Promise<PropertyDetail> {
  return fetchJson(`/v1/properties/${id}`);
}

export function fetchPropertyWorkflow(id: string): Promise<PropertyWorkflow> {
  return fetchJson(`/v1/properties/${id}/workflow`);
}

export async function fetchPropertyMedia(id: string): Promise<readonly PropertyMediaItem[]> {
  const data = await fetchJson<{ items: PropertyMediaItem[] }>(`/v1/properties/${id}/media`);
  return data.items;
}

/**
 * Titulni fotky vice nemovitosti jednim volanim — kanban `/zakazky` by jinak
 * poslal jeden request na kartu (stejny duvod jako u souhrnu nakladu, O6a).
 */
export async function fetchPropertyCovers(
  propertyIds: readonly string[],
): Promise<readonly PropertyCover[]> {
  const ids = [...new Set(propertyIds)].filter((id) => id !== '');
  if (ids.length === 0) return [];
  const data = await fetchJson<{ items: PropertyCover[] }>(
    `/v1/properties/covers?ids=${encodeURIComponent(ids.join(','))}`,
  );
  return data.items;
}

/** Strankovani pri hledani adres — jedna stranka na 200 nemovitosti, nejvys 5 stranek. */
const ADDRESS_PAGE_SIZE = 200;
const ADDRESS_MAX_PAGES = 5;

/**
 * Krok O8: nemovitosti podle id pro obrazovky, ktere o nich nesou jen
 * `propertyId`. Cte JIZ EXISTUJICI seznam nemovitosti (LIST-CONTRACT, tentyz
 * endpoint jako `/nemovitosti`) — zadny novy kontrakt.
 * `/v1/properties/list` neumi filtr podle id, takze se strankuje a konci se
 * hned, jak jsou vsechna hledana id nalezena.
 *
 * Krok UXR5 bod 3: tohle je JEDNO strankovani pro VSECHNY tri veci, ktere z
 * polozky seznamu potrebujeme (adresa, obec, druh nemovitosti). Do UXR5 to
 * byly dve kopie teze smycky vedle sebe a treti by pribyla s „typem a
 * mistem" v „Zakázkách v pohybu" (CLAUDE.md §8.2).
 */
export async function fetchPropertyBriefs(
  propertyIds: readonly string[],
): Promise<ReadonlyMap<string, PropertyListItem>> {
  const wanted = new Set(propertyIds.filter((id) => id !== ''));
  const found = new Map<string, PropertyListItem>();
  if (wanted.size === 0) return found;
  for (let page = 1; page <= ADDRESS_MAX_PAGES; page += 1) {
    const result = await fetchPropertiesList({ page, pageSize: ADDRESS_PAGE_SIZE });
    for (const item of result.items) {
      if (wanted.has(item.id)) found.set(item.id, item);
    }
    if (found.size === wanted.size || page * ADDRESS_PAGE_SIZE >= result.total) break;
  }
  return found;
}

/** Adresy nemovitosti pro zakazky, ktere nemaji pouzitelny nazev (`lib/case-title.ts`). */
export async function fetchPropertyAddresses(
  propertyIds: readonly string[],
): Promise<ReadonlyMap<string, string>> {
  const found = new Map<string, string>();
  for (const [id, item] of await fetchPropertyBriefs(propertyIds)) {
    if (item.address !== '') found.set(id, item.address);
  }
  return found;
}

/**
 * Krok DEMO2: obce nemovitosti (`addresses.locality`) pro karty bez fotky i
 * bez souradnic — z nich se sklada mapovy vyrez obce (`lib/municipality.ts`).
 */
export async function fetchPropertyLocalities(
  propertyIds: readonly string[],
): Promise<ReadonlyMap<string, string>> {
  const found = new Map<string, string>();
  for (const [id, item] of await fetchPropertyBriefs(propertyIds)) {
    if (item.locality !== null && item.locality !== '') found.set(id, item.locality);
  }
  return found;
}

interface UploadMediaResult {
  readonly items: readonly PropertyMediaItem[];
  readonly rejected: readonly { readonly fileName: string; readonly reason: string }[];
}

/** Nahrani fotek (multipart). Odmitnute soubory chodi zvlast, ne jako chyba. */
export async function uploadPropertyMedia(
  id: string,
  files: readonly File[],
): Promise<UploadMediaResult> {
  const form = new FormData();
  for (const file of files) form.append('files', file, file.name);
  const res = await fetch(`${baseUrl}/v1/properties/${id}/media`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    const problem = (await res.json().catch(() => null)) as ProblemBody | null;
    throw new Error(problem?.detail ?? problem?.title ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as UploadMediaResult;
}

async function sendJson<T>(
  path: string,
  // Krok APP3 bod 5: rozhodnuti o rozporu je POST (zaklada zaznam volby).
  method: 'PATCH' | 'PUT' | 'DELETE' | 'POST',
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    method,
    credentials: 'include',
    headers: body === undefined ? { accept: 'application/json' } : { accept: 'application/json', 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!res.ok) {
    const problem = (await res.json().catch(() => null)) as ProblemBody | null;
    throw new Error(problem?.detail ?? problem?.title ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function reorderPropertyMedia(
  id: string,
  order: readonly string[],
): Promise<readonly PropertyMediaItem[]> {
  const data = await sendJson<{ items: PropertyMediaItem[] }>(
    `/v1/properties/${id}/media/order`,
    'PATCH',
    { order },
  );
  return data.items;
}

/**
 * Krok NAP3 bod 1 — NAVRH poradi a popisku fotek (server, `GET …/suggestion`).
 * Jen cteni: zapis dela az potvrzeni maklerem pres `reorderPropertyMedia` +
 * `setPropertyMediaCaption`.
 */
export interface PropertyMediaSuggestionItem {
  readonly mediaId: string;
  readonly sortOrder: number;
  readonly caption: string | null;
  readonly hasQualityIssue: boolean;
}

export async function fetchPropertyMediaSuggestion(
  id: string,
): Promise<readonly PropertyMediaSuggestionItem[]> {
  const data = await fetchJson<{ items: PropertyMediaSuggestionItem[] }>(
    `/v1/properties/${id}/media/suggestion`,
  );
  return data.items;
}

export function setPropertyMediaCover(id: string, mediaId: string): Promise<PropertyMediaItem> {
  return sendJson<PropertyMediaItem>(`/v1/properties/${id}/media/${mediaId}`, 'PATCH', {
    isCover: true,
  });
}

export function setPropertyMediaCaption(
  id: string,
  mediaId: string,
  caption: string,
): Promise<PropertyMediaItem> {
  return sendJson<PropertyMediaItem>(`/v1/properties/${id}/media/${mediaId}`, 'PATCH', { caption });
}

/**
 * Krok PROP7B — OPRAVA ADRESY NEMOVITOSTI (`PATCH /v1/properties/:id/address`,
 * krok PROP7 bod 3). Adresa jde serveru ve TVARU ZALOZENI (`propertyAddressOf`
 * z `address-query.ts`, SSOT vykladu jednoho radku adresy) — „zadej adresu"
 * a „oprav adresu" tak nemohou mit jinou predstavu o platnem vstupu.
 * `reason` je povinny: oprava meni, co uvidi verejna stranka i rozpracovana
 * smlouva, takze audit ma vedet proc.
 */
export function changePropertyAddress(
  id: string,
  address: AddressFromQuery,
  reason: string,
): Promise<ChangeAddressResult> {
  return sendJson<ChangeAddressResult>(`/v1/properties/${id}/address`, 'PATCH', {
    address,
    reason,
  });
}

export function deletePropertyMedia(id: string, mediaId: string): Promise<{ deleted: true }> {
  return sendJson<{ deleted: true }>(`/v1/properties/${id}/media/${mediaId}`, 'DELETE');
}

export async function fetchPropertyDocuments(id: string): Promise<readonly PropertyDocumentItem[]> {
  const data = await fetchJson<{ items: PropertyDocumentItem[] }>(`/v1/properties/${id}/documents`);
  return data.items;
}

export async function fetchPropertyFacts(id: string): Promise<readonly PropertyFact[]> {
  const data = await fetchJson<{ items: PropertyFact[] }>(`/v1/properties/${id}/facts`);
  return data.items;
}

/**
 * Krok APP3 bod 5 (nalez A14, R78 bod 3) — ROZPOR UREDNIHO A RUCNIHO UDAJE.
 *
 * Karta o rozporu mlcela: projekce uredni hodnotu preskocila
 * (`verified_by_user`) a zahodila. Ted je evidovana a makléřka rozhoduje,
 * ktery udaj plati — `manual` ponecha jeji zapis, `registry` zapise uredni
 * hodnotu jako novy fakt. Uredni udaj zustava v evidenci v OBOU pripadech.
 */
export interface PropertyFactConflict {
  readonly id: string;
  readonly propertyId: string;
  readonly factKey: string;
  readonly registryValue: unknown;
  readonly registrySourceType: string;
  readonly registrySourceRef: string | null;
  readonly registryCapturedAt: string;
  readonly verifiedValue: unknown;
  readonly resolution: 'open' | 'manual' | 'registry';
  readonly resolvedBy: string | null;
  readonly resolvedAt: string | null;
  readonly createdAt: string;
}

export async function fetchPropertyFactConflicts(
  id: string,
): Promise<readonly PropertyFactConflict[]> {
  const data = await fetchJson<{ items: PropertyFactConflict[] }>(
    `/v1/properties/${id}/fact-conflicts?open=true`,
  );
  return data.items ?? [];
}

export function resolvePropertyFactConflict(
  id: string,
  factKey: string,
  choice: 'manual' | 'registry',
): Promise<PropertyFactConflict> {
  return sendJson<PropertyFactConflict>(
    `/v1/properties/${id}/fact-conflicts/${encodeURIComponent(factKey)}/resolve`,
    'POST',
    { choice },
  );
}

/**
 * Krok P8F5 bod 3: „Uložit i do zakázky" — hodnota ze smlouvy na kartu
 * nemovitosti. Jen NA KLEPNUTI maklere, nikdy tise: smlouva je dohoda stran,
 * karta je stav nemovitosti, a tichy prepis karty ze smlouvy zadani zakazuje.
 *
 * `factKey` ani hodnotu si obrazovka NESKLADA — oboje prijde ze serveru
 * (`ContractFactChangeDto.saveToCase`), ktery je prevedl tou samou mapou,
 * jakou jede „Doplnit ze zakázky". Puvod je makler (`sourceType: 'user'`,
 * `status: 'UNKNOWN'`) — stejne jako u prevzeti predstavy majitele
 * (`owner-price-expectation.ts`).
 */
export async function savePropertyFactFromContract(
  propertyId: string,
  factKey: string,
  value: unknown,
): Promise<void> {
  await sendJson<unknown>(`/v1/properties/${propertyId}/facts`, 'PUT', {
    factKey,
    value,
    provenance: { status: 'UNKNOWN', sourceType: 'user', sourceRef: 'contract-field' },
  });
}

/** Money fakt (asking_price) z faktu nemovitosti; jine tvary ignoruje. */
export function factMoney(facts: readonly PropertyFact[] | undefined, key: string): Money | null {
  const fact = facts?.find((f) => f.factKey === key);
  if (fact === undefined || typeof fact.value !== 'object' || fact.value === null) return null;
  const value = fact.value as { amount?: unknown; currency?: unknown };
  if (typeof value.amount !== 'string' || typeof value.currency !== 'string') return null;
  return { amount: value.amount, currency: value.currency };
}

/** Formatovani Money jen pro zobrazeni (zadna aritmetika nad amount). */
export function formatMoney(price: Money | null): string | null {
  if (price === null) return null;
  const amount = Number(price.amount);
  if (!Number.isFinite(amount)) return `${price.amount} ${price.currency}`;
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: price.currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Sazba v procentech jen pro zobrazeni (krok QAFIX9b): server posila `numeric`
 * jako retezec, takze DPH prijde „21.00" a obrazovka „Tarif a platba" hlasila
 * „DPH 21.00 %". Nuly za desetinnou carkou se zahazuji, zbytek se formatuje
 * podle locale (ceska desetinna CARKA) — znak `%` patri do textu katalogu,
 * protoze jeho pozice je v kazdem jazyce jina.
 *
 * Nerozpoznatelna hodnota se vraci, jak prisla: lepsi surove cislo nez „NaN".
 */
export function formatPercent(rate: string | null | undefined): string {
  if (rate === null || rate === undefined || rate.trim() === '') return '';
  const value = Number(rate);
  if (!Number.isFinite(value)) return rate;
  return new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 2 }).format(value);
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/** Tvary slova „den“ pro relativni čas (krok UX1) — 1 den se čte „před 1 dnem“. */
const DAY_FORMS = { one: 'dnem', few: 'dny', other: 'dny' } as const;

/**
 * Relativni cas pro UI ("pred 2 h") — jen zobrazeni, "ted" bere prohlizec
 * (systemovy clock je pro backend, viz routes/today.tsx). Nad ~2 dny se
 * vraci absolutni datum, aby stary zaznam nepusobil jako "prave ted".
 */
export function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'právě teď';
  if (diffMin < 60) return `před ${String(diffMin)} min`;
  const diffHours = Math.round(diffMin / 60);
  if (diffHours < 24) return `před ${String(diffHours)} h`;
  const diffDays = Math.round(diffHours / 24);
  // Sklonovani cisel na jednom miste (krok UX1): „před 1 dnem", jinak „před 2 dny".
  if (diffDays <= 2) return `před ${pluralize(diffDays, DAY_FORMS, 'cs')}`;
  return formatDate(iso);
}

export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium' }).format(date);
}

export function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

/** Serverovy CSV export (format=csv) se stejnymi filtry jako seznam. */
export async function downloadPropertiesCsv(query: ListQuery): Promise<void> {
  const params = toSearchParams(query);
  params.set('format', 'csv');
  const res = await fetch(`${baseUrl}/v1/properties/list?${params.toString()}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'nemovitosti.csv';
  anchor.click();
  URL.revokeObjectURL(url);
}
